import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Mail, TrendingUp, TrendingDown, Download, Calendar, Loader2, Award, AlertTriangle, Send, MousePointerClick, Target, DollarSign, UserMinus } from "lucide-react";
import { toast } from "sonner";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  Legend,
  ComposedChart
} from "recharts";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const MONTHS_PT = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

// Stat Card Component
const StatCard = ({ title, value, subtitle, icon: Icon, color = "blue", suffix = "" }) => {
  const colorClasses = {
    blue: { bg: "bg-blue-50", text: "text-blue-600" },
    green: { bg: "bg-emerald-50", text: "text-emerald-600" },
    purple: { bg: "bg-purple-50", text: "text-purple-600" },
    amber: { bg: "bg-amber-50", text: "text-amber-600" },
    red: { bg: "bg-red-50", text: "text-red-600" },
    orange: { bg: "bg-orange-50", text: "text-orange-600" }
  };
  const colors = colorClasses[color];

  return (
    <Card className="stat-card">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between mb-3">
          <div className={`p-3 rounded-xl ${colors.bg}`}>
            <Icon className={`w-6 h-6 ${colors.text}`} strokeWidth={1.5} />
          </div>
        </div>
        <p className="text-sm text-slate-500 mb-1">{title}</p>
        <p className="text-2xl font-bold text-slate-900">{value}{suffix}</p>
        {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
      </CardContent>
    </Card>
  );
};

// Month Comparison Card
const MonthComparisonCard = ({ title, bestMonth, worstMonth, icon: Icon, color = "blue", suffix = "" }) => {
  const colorClasses = {
    blue: { bg: "bg-blue-50", text: "text-blue-600" },
    green: { bg: "bg-emerald-50", text: "text-emerald-600" },
    amber: { bg: "bg-amber-50", text: "text-amber-600" },
    orange: { bg: "bg-orange-50", text: "text-orange-600" }
  };
  const colors = colorClasses[color];

  return (
    <Card className="stat-card">
      <CardContent className="pt-6">
        <div className="flex items-center gap-3 mb-4">
          <div className={`p-2 rounded-lg ${colors.bg}`}>
            <Icon className={`w-5 h-5 ${colors.text}`} />
          </div>
          <p className="font-semibold text-slate-700">{title}</p>
        </div>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
            <div className="flex items-center gap-2">
              <Award className="w-4 h-4 text-emerald-600" />
              <span className="text-sm text-emerald-700">Melhor mês</span>
            </div>
            <div className="text-right">
              <p className="font-bold text-emerald-700">{bestMonth?.month || '-'}</p>
              <p className="text-xs text-emerald-600">{bestMonth?.value || 0}{suffix}</p>
            </div>
          </div>
          <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-600" />
              <span className="text-sm text-red-700">Pior mês</span>
            </div>
            <div className="text-right">
              <p className="font-bold text-red-700">{worstMonth?.month || '-'}</p>
              <p className="text-xs text-red-600">{worstMonth?.value || 0}{suffix}</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const EmailAnnualReportPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [reports, setReports] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState([]);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const response = await axios.get(`${API_URL}/reports/email`);
      setReports(response.data);
      
      const years = [...new Set(response.data.map(r => new Date(r.period_start).getFullYear()))];
      setAvailableYears(years.sort((a, b) => b - a));
      if (years.length > 0 && !years.includes(selectedYear)) {
        setSelectedYear(years[0]);
      }
    } catch (error) {
      toast.error("Erro ao carregar relatórios");
    } finally {
      setLoading(false);
    }
  };

  const yearReports = reports.filter(r => {
    const year = new Date(r.period_start).getFullYear();
    return year === selectedYear;
  });

  // Aggregate data by month
  const monthlyData = MONTHS_PT.map((month, index) => {
    const monthReports = yearReports.filter(r => {
      const reportMonth = new Date(r.period_start).getMonth();
      return reportMonth === index;
    });

    if (monthReports.length === 0) {
      return { month, sent: 0, delivered: 0, openRate: 0, clickRate: 0, unsubRate: 0, conversions: 0, revenue: 0 };
    }

    const totals = monthReports.reduce((acc, r) => ({
      sent: acc.sent + (r.emails_sent || 0),
      delivered: acc.delivered + (r.delivered || 0),
      openRate: acc.openRate + (r.open_rate || 0),
      clickRate: acc.clickRate + (r.click_rate || 0),
      unsubRate: acc.unsubRate + (r.unsubscribe_rate || 0),
      conversions: acc.conversions + (r.conversions || 0),
      revenue: acc.revenue + (r.revenue || 0)
    }), { sent: 0, delivered: 0, openRate: 0, clickRate: 0, unsubRate: 0, conversions: 0, revenue: 0 });

    return {
      month,
      sent: totals.sent,
      delivered: totals.delivered,
      openRate: totals.openRate / monthReports.length,
      clickRate: totals.clickRate / monthReports.length,
      unsubRate: totals.unsubRate / monthReports.length,
      conversions: totals.conversions,
      revenue: totals.revenue
    };
  });

  // Calculate annual totals
  const annualTotals = {
    sent: monthlyData.reduce((sum, m) => sum + m.sent, 0),
    delivered: monthlyData.reduce((sum, m) => sum + m.delivered, 0),
    conversions: monthlyData.reduce((sum, m) => sum + m.conversions, 0),
    revenue: monthlyData.reduce((sum, m) => sum + m.revenue, 0),
    avgOpenRate: monthlyData.filter(m => m.openRate > 0).reduce((sum, m) => sum + m.openRate, 0) / (monthlyData.filter(m => m.openRate > 0).length || 1),
    avgClickRate: monthlyData.filter(m => m.clickRate > 0).reduce((sum, m) => sum + m.clickRate, 0) / (monthlyData.filter(m => m.clickRate > 0).length || 1),
    avgUnsubRate: monthlyData.filter(m => m.unsubRate > 0).reduce((sum, m) => sum + m.unsubRate, 0) / (monthlyData.filter(m => m.unsubRate > 0).length || 1)
  };

  // Find best/worst months
  const findBestWorst = (data, key, higherIsBetter = true) => {
    const filtered = data.filter(m => m[key] > 0);
    if (filtered.length === 0) return { best: null, worst: null };
    
    const sorted = [...filtered].sort((a, b) => higherIsBetter ? b[key] - a[key] : a[key] - b[key]);
    const formatValue = (val) => {
      if (key === 'revenue') return val.toFixed(2) + '€';
      if (key === 'openRate' || key === 'clickRate' || key === 'unsubRate') return val.toFixed(1) + '%';
      return val.toLocaleString('pt-PT');
    };
    return {
      best: { month: sorted[0].month, value: formatValue(sorted[0][key]) },
      worst: { month: sorted[sorted.length - 1].month, value: formatValue(sorted[sorted.length - 1][key]) }
    };
  };

  const openRateComparison = findBestWorst(monthlyData, 'openRate');
  const clickRateComparison = findBestWorst(monthlyData, 'clickRate');
  const revenueComparison = findBestWorst(monthlyData, 'revenue');

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API_URL}/export/email-annual/${user.client_id}?year=${selectedYear}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `relatorio-email-anual-${selectedYear}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF exportado com sucesso");
    } catch (error) {
      toast.error("Erro ao exportar PDF");
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="email-annual-report-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3" style={{ fontFamily: 'Manrope' }}>
            <div className="p-2 bg-emerald-100 rounded-xl">
              <Mail className="w-7 h-7 text-emerald-600" />
            </div>
            Email Marketing - Relatório Anual
          </h1>
          <p className="text-slate-500 mt-2">
            Análise completa de performance de campanhas de email ao longo do ano
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="px-4 py-2 border border-slate-200 rounded-lg bg-white text-slate-700 font-medium"
          >
            {availableYears.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
          <Button 
            onClick={handleExportPDF}
            disabled={exporting || yearReports.length === 0}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Exportar PDF
          </Button>
        </div>
      </div>

      {yearReports.length === 0 ? (
        <Card className="stat-card">
          <CardContent className="py-12 text-center">
            <Calendar className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">Sem dados para {selectedYear}</h3>
            <p className="text-slate-500">Não existem relatórios de Email Marketing para este ano.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Annual Summary */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-slate-800">Resumo Anual {selectedYear}</h2>
              <Separator className="flex-1" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard
                title="Emails Enviados"
                value={annualTotals.sent.toLocaleString('pt-PT')}
                subtitle="Acumulado anual"
                icon={Send}
                color="blue"
              />
              <StatCard
                title="Taxa de Abertura"
                value={annualTotals.avgOpenRate.toFixed(1)}
                subtitle="Média anual"
                icon={Mail}
                color="green"
                suffix="%"
              />
              <StatCard
                title="Taxa de Clique"
                value={annualTotals.avgClickRate.toFixed(1)}
                subtitle="Média anual"
                icon={MousePointerClick}
                color="purple"
                suffix="%"
              />
              <StatCard
                title="Receita Total"
                value={annualTotals.revenue.toLocaleString('pt-PT', { minimumFractionDigits: 2 })}
                subtitle="Acumulado anual"
                icon={DollarSign}
                color="amber"
                suffix="€"
              />
            </div>
          </div>

          {/* Best/Worst Months */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-slate-800">Comparação Mensal</h2>
              <Separator className="flex-1" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <MonthComparisonCard
                title="Taxa de Abertura"
                bestMonth={openRateComparison.best}
                worstMonth={openRateComparison.worst}
                icon={Mail}
                color="green"
              />
              <MonthComparisonCard
                title="Taxa de Clique"
                bestMonth={clickRateComparison.best}
                worstMonth={clickRateComparison.worst}
                icon={MousePointerClick}
                color="amber"
              />
              <MonthComparisonCard
                title="Receita"
                bestMonth={revenueComparison.best}
                worstMonth={revenueComparison.worst}
                icon={DollarSign}
                color="blue"
              />
            </div>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Emails Sent & Delivered */}
            <Card className="stat-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Send className="w-5 h-5 text-blue-600" />
                  Emails Enviados e Entregues
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="month" stroke="#94A3B8" fontSize={12} />
                    <YAxis stroke="#94A3B8" fontSize={12} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #E2E8F0', borderRadius: '8px' }}
                    />
                    <Legend />
                    <Bar dataKey="sent" fill="#2563EB" name="Enviados" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="delivered" fill="#10B981" name="Entregues" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Engagement Rates */}
            <Card className="stat-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Target className="w-5 h-5 text-emerald-600" />
                  Taxas de Engagement
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="month" stroke="#94A3B8" fontSize={12} />
                    <YAxis stroke="#94A3B8" fontSize={12} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #E2E8F0', borderRadius: '8px' }}
                      formatter={(value) => `${value.toFixed(2)}%`}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="openRate" stroke="#10B981" strokeWidth={2} name="Taxa Abertura (%)" dot={{ fill: '#10B981' }} />
                    <Line type="monotone" dataKey="clickRate" stroke="#7C3AED" strokeWidth={2} name="Taxa Clique (%)" dot={{ fill: '#7C3AED' }} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Conversions & Revenue */}
            <Card className="stat-card lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <DollarSign className="w-5 h-5 text-amber-600" />
                  Conversões e Receita
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="month" stroke="#94A3B8" fontSize={12} />
                    <YAxis yAxisId="left" stroke="#2563EB" fontSize={12} />
                    <YAxis yAxisId="right" orientation="right" stroke="#F59E0B" fontSize={12} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #E2E8F0', borderRadius: '8px' }}
                      formatter={(value, name) => [
                        name === 'Receita (€)' ? `${value.toFixed(2)}€` : value.toLocaleString('pt-PT'),
                        name
                      ]}
                    />
                    <Legend />
                    <Bar yAxisId="left" dataKey="conversions" fill="#2563EB" name="Conversões" radius={[4, 4, 0, 0]} />
                    <Line yAxisId="right" type="monotone" dataKey="revenue" stroke="#F59E0B" strokeWidth={3} name="Receita (€)" dot={{ fill: '#F59E0B', r: 4 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Monthly Table */}
          <Card className="stat-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Calendar className="w-5 h-5 text-slate-600" />
                Dados Mensais Detalhados
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 font-semibold text-slate-600">Mês</th>
                      <th className="text-right py-3 px-4 font-semibold text-slate-600">Enviados</th>
                      <th className="text-right py-3 px-4 font-semibold text-slate-600">Entregues</th>
                      <th className="text-right py-3 px-4 font-semibold text-slate-600">Open Rate</th>
                      <th className="text-right py-3 px-4 font-semibold text-slate-600">Click Rate</th>
                      <th className="text-right py-3 px-4 font-semibold text-slate-600">Unsub Rate</th>
                      <th className="text-right py-3 px-4 font-semibold text-slate-600">Conversões</th>
                      <th className="text-right py-3 px-4 font-semibold text-slate-600">Receita</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthlyData.map((data, index) => (
                      <tr key={data.month} className={`border-b border-slate-100 ${index % 2 === 0 ? 'bg-slate-50/50' : ''}`}>
                        <td className="py-3 px-4 font-medium text-slate-700">{data.month}</td>
                        <td className="text-right py-3 px-4 text-slate-600">{data.sent.toLocaleString('pt-PT')}</td>
                        <td className="text-right py-3 px-4 text-slate-600">{data.delivered.toLocaleString('pt-PT')}</td>
                        <td className="text-right py-3 px-4 text-slate-600">{data.openRate > 0 ? `${data.openRate.toFixed(1)}%` : '-'}</td>
                        <td className="text-right py-3 px-4 text-slate-600">{data.clickRate > 0 ? `${data.clickRate.toFixed(1)}%` : '-'}</td>
                        <td className="text-right py-3 px-4 text-slate-600">{data.unsubRate > 0 ? `${data.unsubRate.toFixed(2)}%` : '-'}</td>
                        <td className="text-right py-3 px-4 text-slate-600">{data.conversions.toLocaleString('pt-PT')}</td>
                        <td className="text-right py-3 px-4 text-slate-600">{data.revenue > 0 ? `${data.revenue.toFixed(2)}€` : '-'}</td>
                      </tr>
                    ))}
                    <tr className="bg-emerald-50 font-semibold">
                      <td className="py-3 px-4 text-emerald-700">TOTAL/MÉDIA</td>
                      <td className="text-right py-3 px-4 text-emerald-700">{annualTotals.sent.toLocaleString('pt-PT')}</td>
                      <td className="text-right py-3 px-4 text-emerald-700">{annualTotals.delivered.toLocaleString('pt-PT')}</td>
                      <td className="text-right py-3 px-4 text-emerald-700">{annualTotals.avgOpenRate.toFixed(1)}%</td>
                      <td className="text-right py-3 px-4 text-emerald-700">{annualTotals.avgClickRate.toFixed(1)}%</td>
                      <td className="text-right py-3 px-4 text-emerald-700">{annualTotals.avgUnsubRate.toFixed(2)}%</td>
                      <td className="text-right py-3 px-4 text-emerald-700">{annualTotals.conversions.toLocaleString('pt-PT')}</td>
                      <td className="text-right py-3 px-4 text-emerald-700">{annualTotals.revenue.toFixed(2)}€</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default EmailAnnualReportPage;
